import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config';

interface UnProcessedError {
  id: number;
  errorMessage: string;
}

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: API_URL, timeout: 1000 * 60 * 3 });

// Always add Authorization header - prioritize .env.local, fallback to global axios default
axiosInstance.interceptors.request.use(
  (config) => {
    const envToken = process.env.REACT_APP_AUTH_TOKEN;
    const globalToken = axios.defaults.headers.common.Authorization;
    
    let authHeader = null;

    if (globalToken && typeof globalToken === 'string') {
      // First priority: global axios token (set by AuthContext)
      authHeader = globalToken;      
    } else if (envToken && envToken.trim() !== '') {
      // Second priority: environment token (if not empty)
      authHeader = `Bearer ${envToken}`;
    }
    
    if (authHeader) {
      config.headers = config.headers || {};
      config.headers.Authorization = authHeader;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data.unProcessed && response.data.unProcessed.length) {
      response.data.unProcessed.forEach(
        (msg: UnProcessedError) => msg.errorMessage && toast.error(msg.errorMessage)
      );
    }
    return response;
  },

  (error) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data) {
      if (error?.response?.status === 500) {
        toast.error('An Error occurred, Please contact your system administrator.');
      } else {
        toast.error(data);
      }
    } else if (data?.errors && typeof data?.errors === 'object') {
      const messages: string[][] = Object.values(data.errors);
      messages.forEach(
        (keyMsgs) => Array.isArray(keyMsgs) && keyMsgs.forEach((msg) => msg && toast.error(msg))
      );
    }
    if (error.message === 'canceled') {
      return Promise.reject();
    }
    return Promise.reject((error.response && data) || 'Something went wrong');
  }
);

export default axiosInstance;
